
import type { Configuration } from '@empathybroker/tsclient-play/dist/models/configuration'
import type { Facet as PlayFacet } from '@empathybroker/tsclient-play/dist/models/facet'
import type { FacetValue as PlayFacetValue } from '@empathybroker/tsclient-play/dist/models/facet-value'
import type { PlayErrorField } from '@empathybroker/tsclient-play/dist/models/play-error-field'
import type { RankingElement } from '@empathybroker/tsclient-play/dist/models/ranking-element'
import type { Facet as SearchFacet } from '@empathybroker/tsclient-play/dist/models/search/facet'
import type { Result } from '@empathybroker/tsclient-play/dist/models/search/result'
import type { Component, PropType } from 'vue/types/options'
import type { Pagination } from '../../models/components/pagination'

import type { RankingElementWithId } from '../../models/components/ranking-element-with-id'
import type { RegularFilter } from '../../models/store/filters/filter'
import { RankingActionEnum } from '@empathybroker/tsclient-play/dist/enums/ranking-action.enum'

import Vue, { defineComponent } from 'vue'

import { mapGetters } from 'vuex'

import { TYPES } from '../../config/inversify.types'

import {
  ColorEnum,
  ConfigurationToolIdEnum,
  DeviceEnum,
  FilterIdEnum,
  RestrictedValueEnum,
  SearchDataLoadTypeEnum,
} from '../../enums'
import { capitalizeSentence } from '../../filters/capitalize-sentence.filter'
import { ConfigurationsFormMixin, RestrictedMixin } from '../../mixins'

import { CONFIG_CONSTS } from '../../store/config/config.consts'

import { ConfigurationsUtils, FiltersUtils } from '../../utils'

import CrudForm from '../crud/crud-form.component.vue'
import EnabledField from '../crud/enabled-field.component.vue'
import DatePicker from '../date-picker.component.vue'
import FacetsSettingsList from '../facets-settings-list.component.vue'
import RegularFilterSelector from '../filters/regular-filter-selector.component.vue'
import { getDatepickerI18nConfig, UiRippleInk } from '../keen-ui'
import RankingResultCardActions from '../ranking/ranking-result-card-actions.component.vue'
import ResultsGrid from '../results-grid.component.vue'
import { EpbBadge } from '../shared/epb-badge'
import { EpbButton } from '../shared/epb-button'
import { EpbSwitch } from '../shared/epb-switch'
import { EpbText } from '../shared/epb-text'
import { EpbTextbox } from '../shared/epb-textbox'
import { EpbToggle } from '../shared/epb-toggle'
import TransitionSize from '../transition-size.component.vue'

import AttributeRankingElementAside from './attribute-ranking-element-aside.component.vue'
import AttributesRanking from './attributes-ranking.component.vue'
import ConfigurationsResultCardFooter from './configurations-result-card-footer.component.vue'
import FacetsConfiguration from './facets-configuration.component.vue'
import ProductRankingElementAside from './product-ranking-element-aside.component.vue'
import ProductsRanking from './products-ranking-list.component.vue'
import QuickRanking from './quick-ranking.component.vue'

export default defineComponent({
  name: 'ConfigurationsForm',
  components: {
    CrudForm,
    EnabledField,
    EpbToggle,
    RegularFilterSelector,
    EpbText,
    EpbTextbox,
    TransitionSize,
    EpbButton,
    EpbSwitch,
    FacetsSettingsList,
    EpbBadge,
    UiRippleInk,
    ResultsGrid,
    ConfigurationsResultCardFooter,
    RankingResultCardActions,
    QuickRanking,
    DatePicker,
  },
  mixins: [RestrictedMixin],
  inject: {
    objectIdService: TYPES.ObjectIdService,
  },
  props: {
    item: {
      type: Object as PropType<Configuration>,
      required: true,
    },
    readOnly: Boolean,
    pivots: {
      type: Array as PropType<Array<RegularFilter>>,
      required: true,
    },
    filters: {
      type: Array as PropType<Array<RegularFilter>>,
      required: true,
    },
    tools: {
      type: Array as PropType<Array<ConfigurationToolIdEnum>>,
      required: true,
    },
    searchData: {
      type: Object as PropType<{
        results: Array<Result>
        facets: Array<SearchFacet>
        loading: { results: boolean; facets: boolean }
      }>,
      required: true,
    },
    searchFilters: {
      type: Array as PropType<Array<RegularFilter>>,
      default: () => [],
    },
    pagination: {
      type: Object as PropType<Pagination>,
      required: true,
    },
    loadSearchDataEnabled: Boolean,
    acceptActionDisabled: Boolean,
    errorsFields: Array as PropType<Array<PlayErrorField>>,
    messages: {
      type: Object,
      required: true,
    },
    searchDate: {
      type: Date,
      default: () => new Date(),
    },
  },
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      instanceConfig: CONFIG_CONSTS.getters.currentInstance,
    }),
    restricted(): string {
      const restrictedValue = this.getRestrictedValueFromObject(this.item.restricted)
      return restrictedValue === RestrictedValueEnum.NONE
        ? RestrictedValueEnum.NONE
        : RestrictedValueEnum.WRITE
    },
    restrictedTooltip(): string {
      const restrictedMessages = this.messages.fields.restricted
      const defaultRole = restrictedMessages.roles.default.toUpperCase()
      if (this.restricted === RestrictedValueEnum.NONE) {
        return this.$epb.utils.compileMessage(restrictedMessages.values[RestrictedValueEnum.READ], {
          roles: defaultRole,
        })
      } else {
        const readMessage: string = this.$epb.utils.compileMessage(
          restrictedMessages.values[RestrictedValueEnum.READ],
          { roles: defaultRole },
        )
        const writeMessage: string = this.$epb.utils.compileMessage(
          restrictedMessages.values[RestrictedValueEnum.WRITE],
          { roles: this.item.restricted.role.join(', ') },
        )
        return `${readMessage}<br>${writeMessage}`
      }
    },
    isFacetsPreviewVisible(): boolean {
      return this.areFacetsConfigured && this.showFacetsPreview
    },
    areFacetsConfigured(): boolean {
      return !!this.item.facets.elements.length
    },
    facets(): SearchFacet[] {
      return this.searchData.facets
        .filter((facet: SearchFacet) => !!this.getItemFacet(facet.id))
        .sort((facet1: SearchFacet, facet2: SearchFacet) => {
          let indexOfItemFacet1 = this.getIndexOfItemFacet(facet1.id)
          if (indexOfItemFacet1 < 0) {
            indexOfItemFacet1 = this.searchData.facets.length
          }
          let indexOfItemFacet2 = this.getIndexOfItemFacet(facet2.id)
          if (indexOfItemFacet2 < 0) {
            indexOfItemFacet2 = this.searchData.facets.length
          }
          return indexOfItemFacet1 - indexOfItemFacet2
        })
    },
    results(): any[] {
      return this.searchData.results.map((result: Result) => {
        const resultRankingConfig = this.item.ranking.products.elements.find(
          (element: RankingElement) => element.fieldValue === result.id,
        )
        return {
          ...result,
          status: resultRankingConfig ? resultRankingConfig.action : undefined,
        }
      })
    },
    resultsGridItemsPerRow(): number {
      if (this.$epb.device <= DeviceEnum.MOBILE) {
        return 1
      }
      if (this.$epb.device <= DeviceEnum.TABLET) {
        return 2
      }
      if (!this.settingsSectionCollapsed && this.isFacetsPreviewVisible) {
        return 3
      }
      return 4
    },
    rankingElementAsideComponent(): Component {
      return {
        [ConfigurationToolIdEnum.ATTRIBUTES_RANKING]: AttributeRankingElementAside,
        [ConfigurationToolIdEnum.PRODUCTS_RANKING]: ProductRankingElementAside,
      }[this.selectedRankingElement.toolId]
    },
    rankingElementAsideMessages() {
      return {
        [ConfigurationToolIdEnum.ATTRIBUTES_RANKING]: this.messages.fields.attributes,
        [ConfigurationToolIdEnum.PRODUCTS_RANKING]: this.messages.fields.products,
      }[this.selectedRankingElement.toolId]
    },
    datepickerProps(): Record<string, any> {
      return {
        ...getDatepickerI18nConfig(this.$epb.locale),
        orientation: this.$epb.device > DeviceEnum.MOBILE ? 'landscape' : 'portrait',
        pickerType: this.$epb.device > DeviceEnum.MOBILE ? 'popover' : 'modal',
        messages: this.$epb.messages.app.date,
      }
    },
    productRankingScheduleActiveMap(): Record<string, boolean> {
      const scheduleActiveMap = {}
      this.item.ranking.products.elements.forEach(
        item =>
          (scheduleActiveMap[item.fieldValue] =
            ConfigurationsFormMixin.methods.isRankingElementMatchingWithDate.bind(this)(item)),
      )
      return scheduleActiveMap
    },
  },
  data() {
    return {
      restrictedOptions: [
        {
          id: RestrictedValueEnum.WRITE,
          content: 'lock',
        },
        {
          id: RestrictedValueEnum.NONE,
          content: 'unlock',
        },
      ],
      settingsSectionCollapsed: false,
      expandedToolConfigId: undefined,
      editionStarted: false,
      selectedRankingElement: undefined,
      showFacetsPreview: true,
      scheduleDate: undefined,
      ColorEnum,
      capitalizeSentence,
      RankingActionEnum,
      SearchDataLoadTypeEnum,
      ConfigurationToolIdEnum,
      FilterIdEnum,
    }
  },
  watch: {
    item: {
      handler(item): void {
        if (
          !this.editionStarted &&
          (!!item.facets.elements.length ||
            !!item.ranking.attributes.elements.length ||
            !!item.ranking.products.elements.length)
        ) {
          this.editionStarted = true
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    onRestrictedChange(selectedRestrictedValue: RestrictedValueEnum) {
      this.item.restricted = this.getRestrictedObjectFromValue(selectedRestrictedValue)
    },
    getToolIcon(toolId: ConfigurationToolIdEnum): string {
      return {
        [ConfigurationToolIdEnum.FACETS_CONFIG]: 'facets',
        [ConfigurationToolIdEnum.ATTRIBUTES_RANKING]: 'attributes',
        [ConfigurationToolIdEnum.PRODUCTS_RANKING]: 'boostbury',
      }[toolId]
    },
    getToolMessages(toolId: ConfigurationToolIdEnum) {
      return {
        [ConfigurationToolIdEnum.FACETS_CONFIG]: this.messages.fields.facets,
        [ConfigurationToolIdEnum.ATTRIBUTES_RANKING]: this.messages.fields.attributes,
        [ConfigurationToolIdEnum.PRODUCTS_RANKING]: this.messages.fields.products,
      }[toolId]
    },
    getToolCount(toolId: ConfigurationToolIdEnum): number {
      return {
        [ConfigurationToolIdEnum.FACETS_CONFIG]: this.item.facets.elements.length,
        [ConfigurationToolIdEnum.ATTRIBUTES_RANKING]: this.item.ranking.attributes.elements.length,
        [ConfigurationToolIdEnum.PRODUCTS_RANKING]: this.item.ranking.products.elements.length,
      }[toolId]
    },
    isToolConfigExpanded(toolId: ConfigurationToolIdEnum): boolean {
      return !this.editionStarted || toolId === this.expandedToolConfigId
    },
    toggleToolConfigExpanded(toolId: ConfigurationToolIdEnum): void {
      this.expandedToolConfigId = toolId !== this.expandedToolConfigId ? toolId : undefined
    },
    getToolConfigComponent(toolId: ConfigurationToolIdEnum): Component {
      return {
        [ConfigurationToolIdEnum.FACETS_CONFIG]: FacetsConfiguration,
        [ConfigurationToolIdEnum.ATTRIBUTES_RANKING]: AttributesRanking,
        [ConfigurationToolIdEnum.PRODUCTS_RANKING]: ProductsRanking,
      }[toolId]
    },
    getToolConfigComponentProps(toolId: ConfigurationToolIdEnum) {
      return {
        [ConfigurationToolIdEnum.FACETS_CONFIG]: {
          item: this.item,
          messages: this.messages.fields.facets,
        },
        [ConfigurationToolIdEnum.ATTRIBUTES_RANKING]: {
          rankingElements: this.item.ranking.attributes.elements,
          messages: this.messages.fields.attributes,
          pivots: this.pivots.map(pivot => {
            return {
              name: FiltersUtils.getEncodedId(pivot.config),
              values: pivot.selectedValues.map(value =>
                FiltersUtils.encodeValue(pivot.config, value),
              ),
            }
          }),
        },
        [ConfigurationToolIdEnum.PRODUCTS_RANKING]: {
          rankingElements: this.item.ranking.products.elements,
          messages: this.messages.fields.products,
        },
      }[toolId]
    },
    onRankingElementEdit(element: RankingElement, toolId: ConfigurationToolIdEnum): void {
      this.selectedRankingElement = {
        element,
        toolId,
      }
    },
    onUpdateRankingElements(
      elements: Array<RankingElement>,
      toolId: ConfigurationToolIdEnum,
    ): void {
      const update = {
        [ConfigurationToolIdEnum.ATTRIBUTES_RANKING]: () => {
          this.item.ranking.attributes.elements = elements
        },
        [ConfigurationToolIdEnum.PRODUCTS_RANKING]: () => {
          this.item.ranking.products.elements = elements
        },
      }

      update[toolId]?.()
    },
    onStartEditionClick(toolId: ConfigurationToolIdEnum): void {
      this.editionStarted = true
      this.toggleToolConfigExpanded(toolId)
    },
    getIndexOfItemFacet(facetId: string): number {
      return this.item.facets.elements.findIndex((itemFacet: PlayFacet) => itemFacet.id === facetId)
    },
    getFacetName(facetId: string): string {
      const facet = this.item.facets.elements.find(
        (itemFacet: PlayFacet) => itemFacet.id === facetId,
      )
      return (!!facet && facet.prettyName) || facetId
    },
    getFacetValueActions(facetId: string, facetValueId: string): RankingActionEnum[] {
      if (this.readOnly) {
        const itemFacetValue = this.getItemFacetValue(facetId, facetValueId)
        if (itemFacetValue) {
          return [itemFacetValue.action]
        }
        return []
      }
      return Object.values(RankingActionEnum)
    },
    isFacetValueActionSelected(
      facetId: string,
      facetValueId: string,
      action: RankingActionEnum,
    ): boolean {
      const itemFacetValue = this.getItemFacetValue(facetId, facetValueId)
      return !!itemFacetValue && itemFacetValue.action === action
    },
    getItemFacetValue(facetId: string, facetValueId: string): PlayFacetValue {
      const itemFacet = this.getItemFacet(facetId)
      if (!!itemFacet && !!itemFacet.values) {
        return itemFacet.values.find(
          (itemFacetValue: PlayFacetValue) => itemFacetValue.id === facetValueId,
        )
      }
    },
    getItemFacet(facetId: string): PlayFacet {
      return this.item.facets.elements.find((itemFacet: PlayFacet) => itemFacet.id === facetId)
    },
    getFacetValueActionColor(
      facetId: string,
      facetValueId: string,
      action: RankingActionEnum,
    ): ColorEnum {
      if (this.isFacetValueActionSelected(facetId, facetValueId, action)) {
        return {
          [RankingActionEnum.BOOST]: ColorEnum.GREEN_75,
          [RankingActionEnum.BURY]: ColorEnum.RED_75,
        }[action]
      } else {
        return ColorEnum.NEUTRAL_75
      }
    },
    onFacetValueActionClick(
      facetId: string,
      facetValueId: string,
      action: RankingActionEnum,
    ): void {
      const itemFacetValue = this.getItemFacetValue(facetId, facetValueId)
      const itemFacet = this.getItemFacet(facetId)
      if (!itemFacetValue) {
        if (!itemFacet.values) {
          itemFacet.values = []
        }
        itemFacet.values.push({
          id: facetValueId,
          action,
        })
      } else {
        if (itemFacetValue.action === action) {
          itemFacet.values.splice(itemFacet.values.indexOf(itemFacetValue), 1)
        } else {
          itemFacetValue.action = action
        }
      }
    },
    onResultStatusChange(result, status: RankingActionEnum): void {
      this.item.ranking.products.elements = this.item.ranking.products.elements.filter(
        (element: RankingElement) => element.fieldValue !== result.id,
      )
      if (status) {
        this.item.ranking.products.elements.push({
          fieldName: this.instanceConfig.catalog.resultMappings.id,
          fieldValue: result.id,
          action: status,
          filters: {},
          schedule: {},
        } as RankingElement)
      }
      if (!this.editionStarted) {
        this.toggleToolConfigExpanded(ConfigurationToolIdEnum.PRODUCTS_RANKING)
      }
    },
    onRankingElementUpdate(element: RankingElementWithId): void {
      const itemRanking = {
        [ConfigurationToolIdEnum.ATTRIBUTES_RANKING]: this.item.ranking.attributes,
        [ConfigurationToolIdEnum.PRODUCTS_RANKING]: this.item.ranking.products,
      }[this.selectedRankingElement.toolId]
      const newElements = [...itemRanking.elements]
      const elementIndex = itemRanking.elements.findIndex(
        (itemElement: RankingElement) =>
          ConfigurationsUtils.getRankingElementId(itemElement) === element.id,
      )
      newElements.splice(elementIndex, 1, element)
      Vue.set(itemRanking, 'elements', newElements)
    },
    onQuickRankingAccept(elements: RankingElement[]): void {
      this.item.ranking.products.elements.push(...elements)
      if (!this.editionStarted) {
        this.toggleToolConfigExpanded(ConfigurationToolIdEnum.PRODUCTS_RANKING)
      }
    },
    isResultStatusActive(id: string): boolean {
      return this.productRankingScheduleActiveMap[id]
    },
  },
})
